/**
 * useCallWakeLock — Prevents the screen from sleeping during an active call.
 *
 * Uses the Screen Wake Lock API (supported in Chrome/Safari 16.4+).
 * Falls back silently on unsupported browsers.
 *
 * Also handles reconnection when the tab becomes visible again after being
 * backgrounded (e.g. phone screen unlock or tab switch).
 */

import { useEffect, useRef } from "react";
import { useMessengerStore } from "../stores/messenger-store";

export function useCallWakeLock() {
  const activeCall = useMessengerStore((s) => s.activeCall);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    if (!activeCall) {
      // Release wake lock when call ends
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
      return;
    }

    // Acquire wake lock
    async function acquireWakeLock() {
      if (!("wakeLock" in navigator)) return;
      try {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
      } catch {
        // Permission denied or not supported — ignore
      }
    }

    void acquireWakeLock();

    // Re-acquire after visibility change (screen unlock / tab return)
    function handleVisibilityChange() {
      if (document.visibilityState === "visible" && useMessengerStore.getState().activeCall) {
        void acquireWakeLock();
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
    };
  }, [activeCall?.id]); // re-run on new call, not on every state update
}
